/**
 * Dashboard de Acompanhamento de Campanhas Win-Back
 * Exibe métricas de tráfego e conversão dos links UTM trackeados
 * DADOS LOCAIS: Usa a tabela tracking_links do banco SQLite
 */
import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Spinner, Alert, Badge, Table, ProgressBar, Form, Button } from 'react-bootstrap';
import { fetchWinBackStats, fetchCampaigns } from '../../services/api';
import styles from './WinBackDashboard.module.css';

const WinBackDashboard = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [data, setData] = useState(null);
    const [campanhas, setCampanhas] = useState([]);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        setError(null);
        try {
            // Buscar dados locais de tracking (não do GA4)
            const [statsResponse, campanhasResponse] = await Promise.all([
                fetchWinBackStats(),
                fetchCampaigns()
            ]);

            if (statsResponse.success) {
                setData(statsResponse);
            } else {
                setError(statsResponse.message || 'Erro ao carregar dados');
            }

            if (campanhasResponse.success) {
                setCampanhas(campanhasResponse.data || []);
            }
        } catch (err) {
            setError('Erro de conexão com o servidor');
            console.error('[WinBack Dashboard] Erro:', err);
        } finally {
            setLoading(false);
        }
    };

    // Calcular métricas agregadas do banco local
    const getMetrics = () => {
        if (!data?.dashboard) {
            return {
                totalClicks: 0,
                totalClientes: 0,
                totalConversions: 0,
                totalRevenue: 0,
                conversionRate: 0
            };
        }

        const { totalAcessos, totalClientesAcessaram, totalConversoes, taxaConversao, receitaRecuperada } = data.dashboard;

        return {
            totalClicks: totalAcessos || 0,
            totalClientes: totalClientesAcessaram || 0,
            totalConversions: totalConversoes || 0,
            totalRevenue: receitaRecuperada || 0,
            conversionRate: parseFloat(taxaConversao) || 0
        };
    };

    const formatNumber = (num) => {
        if (!num) return '0';
        return new Intl.NumberFormat('pt-BR').format(num);
    };

    const formatCurrency = (num) => {
        if (!num) return 'R$ 0,00';
        return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(num);
    };

    if (loading) {
        return (
            <div className={styles.loadingContainer}>
                <Spinner animation="border" variant="primary" />
                <p className="mt-3 text-muted">Carregando métricas de campanhas...</p>
            </div>
        );
    }

    if (error) {
        return (
            <Alert variant="danger">
                <Alert.Heading>Erro ao carregar dados</Alert.Heading>
                <p>{error}</p>
            </Alert>
        );
    }

    const metrics = getMetrics();
    const detalhes = data?.detalhes || [];

    return (
        <div className={styles.container}>
            {/* Header */}
            <div className={styles.header}>
                <div className={styles.headerInfo}>
                    <h5 className={styles.title}>📊 Dashboard Win-Back</h5>
                    <p className={styles.subtitle}>
                        Acompanhe o desempenho dos links trackeados enviados via WhatsApp e Email
                    </p>
                </div>
                <div className={styles.headerControls}>
                    <Button variant="outline-primary" size="sm" onClick={loadData}>
                        🔄 Atualizar
                    </Button>
                </div>
            </div>

            {/* Stats Cards */}
            <Row className="g-3 mb-4">
                <Col md={3}>
                    <Card className={styles.statCard}>
                        <Card.Body className="text-center">
                            <div className={styles.statIcon}>🔗</div>
                            <div className={styles.statValue}>{formatNumber(metrics.totalClicks)}</div>
                            <div className={styles.statLabel}>Total de Cliques</div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card className={styles.statCard}>
                        <Card.Body className="text-center">
                            <div className={styles.statIcon}>👥</div>
                            <div className={styles.statValue}>{formatNumber(metrics.totalClientes)}</div>
                            <div className={styles.statLabel}>Clientes Acessaram</div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card className={styles.statCard}>
                        <Card.Body className="text-center">
                            <div className={styles.statIcon}>🛒</div>
                            <div className={styles.statValue}>{formatNumber(metrics.totalConversions)}</div>
                            <div className={styles.statLabel}>Conversões</div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card className={`${styles.statCard} ${styles.statCardHighlight}`}>
                        <Card.Body className="text-center">
                            <div className={styles.statIcon}>💰</div>
                            <div className={styles.statValue}>{formatCurrency(metrics.totalRevenue)}</div>
                            <div className={styles.statLabel}>Receita Recuperada</div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Conversion Rate */}
            <Row className="mb-4">
                <Col>
                    <Card className={styles.conversionCard}>
                        <Card.Body>
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <span className="fw-bold">Taxa de Conversão</span>
                                <Badge bg={metrics.conversionRate >= 3 ? 'success' : metrics.conversionRate >= 1 ? 'warning' : 'danger'}>
                                    {metrics.conversionRate.toFixed(2)}%
                                </Badge>
                            </div>
                            <ProgressBar
                                now={Math.min(metrics.conversionRate * 10, 100)}
                                variant={metrics.conversionRate >= 3 ? 'success' : metrics.conversionRate >= 1 ? 'warning' : 'danger'}
                                className={styles.conversionBar}
                            />
                            <small className="text-muted">
                                Meta: 3% | {formatNumber(metrics.totalClientes)} clientes acessaram → {formatNumber(metrics.totalConversions)} compraram
                            </small>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Clientes que Acessaram */}
            {detalhes.length === 0 ? (
                <Alert variant="info" className="text-center">
                    <div style={{ fontSize: '3rem' }}>📊</div>
                    <h5 className="mt-3">Nenhum clique registrado ainda</h5>
                    <p className="text-muted mb-0">
                        Comece enviando links trackeados para seus clientes inativos via WhatsApp ou Email.
                        Os dados aparecerão aqui assim que os clientes clicarem nos links.
                    </p>
                </Alert>
            ) : (
                <Card className={styles.tableCard}>
                    <Card.Header className={styles.tableHeader}>
                        <strong>📋 Clientes que Acessaram os Links</strong>
                        <Badge bg="primary" className="ms-2">{detalhes.length}</Badge>
                    </Card.Header>
                    <Card.Body className="p-0">
                        <Table hover responsive className={styles.campaignsTable}>
                            <thead>
                                <tr>
                                    <th>Cliente</th>
                                    <th>Cliques</th>
                                    <th>Último Acesso</th>
                                    <th>Comprou?</th>
                                    <th>Pedidos</th>
                                    <th>Valor Recuperado</th>
                                </tr>
                            </thead>
                            <tbody>
                                {detalhes.map((cliente, idx) => (
                                    <tr key={idx}>
                                        <td>
                                            <div className="fw-bold">{cliente.nome || 'Cliente Desconhecido'}</div>
                                            <small className="text-muted">{cliente.email || ''}</small>
                                        </td>
                                        <td>
                                            <Badge bg="info">{cliente.total_cliques || 0}</Badge>
                                        </td>
                                        <td>
                                            <small>{cliente.ultimo_acesso ? new Date(cliente.ultimo_acesso).toLocaleString('pt-BR') : '-'}</small>
                                        </td>
                                        <td>
                                            {cliente.comprou ? (
                                                <Badge bg="success">✅ Sim</Badge>
                                            ) : (
                                                <Badge bg="secondary">❌ Não</Badge>
                                            )}
                                        </td>
                                        <td>{cliente.qtd_pedidos || 0}</td>
                                        <td className="fw-bold text-success">
                                            {formatCurrency(cliente.valor_recuperado || 0)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </Card.Body>
                </Card>
            )}

            {/* Campanhas Criadas */}
            {campanhas.length > 0 && (
                <Card className={`${styles.tableCard} mt-4`}>
                    <Card.Header className={styles.tableHeader}>
                        <strong>🚀 Campanhas Criadas</strong>
                        <Badge bg="primary" className="ms-2">{campanhas.length}</Badge>
                    </Card.Header>
                    <Card.Body className="p-0">
                        <Table hover responsive className={styles.campaignsTable}>
                            <thead>
                                <tr>
                                    <th>Nome</th>
                                    <th>Data</th>
                                    <th>Clientes</th>
                                </tr>
                            </thead>
                            <tbody>
                                {campanhas.slice(0, 10).map((camp, idx) => (
                                    <tr key={idx}>
                                        <td className="fw-bold">{camp.nome}</td>
                                        <td><small>{new Date(camp.data_criacao).toLocaleDateString('pt-BR')}</small></td>
                                        <td><Badge bg="info">{camp.total_clientes || 0}</Badge></td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </Card.Body>
                </Card>
            )}

            {/* Tips */}
            <Card className={`${styles.tipsCard} mt-4`}>
                <Card.Body>
                    <h6>💡 Dicas para Melhorar Resultados</h6>
                    <ul className="mb-0 small">
                        <li><strong>WhatsApp:</strong> Envie mensagens personalizadas mencionando o produto favorito do cliente</li>
                        <li><strong>Email:</strong> Use assuntos atrativos como "Sentimos sua falta!" ou "Oferta especial para você"</li>
                        <li><strong>Timing:</strong> Envie para clientes entre 30-90 dias sem comprar para melhor conversão</li>
                        <li><strong>Segmentação:</strong> Foque em clientes com maior ticket médio histórico</li>
                    </ul>
                </Card.Body>
            </Card>
        </div>
    );
};

export default WinBackDashboard;
